import type { RefObject } from 'react';
import { BottomSheet, Dialog } from '../components/BottomSheet';

export type UnsavedChangesSheetProps = {
  onSave: () => void;
  onDiscard: () => void;
  onKeepEditing: () => void;
  /** The element focus should return to (typically the wardrobe's Close button — the most common
   *  trigger of a blocked navigation) — see BottomSheet.tsx's own doc comment. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
};

/**
 * Task 15 unsaved-changes guard, shown while `useBlocker` (WardrobeScreen.tsx) has intercepted a
 * navigation away from a dirty wardrobe — the Close button included, since it navigates through
 * the same router `useBlocker` watches (see WardrobeScreen.tsx's own comment on why that needs
 * no special-casing). Copy is deliberately honest per the brief: equip/unequip/dye already
 * committed to the server the instant they happened (§11.3's optimistic `usePutOutfit`) —
 * "unsaved" here means "not yet saved as a Look", nothing is actually at risk of being lost by
 * leaving, which is exactly why Discard has nothing to roll back.
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3). Unlike
 * `DyeSheet`/`SaveLookSheet`, there's no single natural trigger button (a blocked navigation can
 * come from the Close button OR the browser's own back gesture, which has no DOM element at
 * all) — callers still pass `restoreFocusRef` pointing at the most sensible default (the Close
 * button) rather than leaving it to Radix's own less reliable generic fallback.
 */
export function UnsavedChangesSheet({
  onSave, onDiscard, onKeepEditing, restoreFocusRef, portalContainer,
}: UnsavedChangesSheetProps) {
  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onKeepEditing(); }}
      contentClassName="unsaved-guard-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
    >
      <Dialog.Title asChild>
        <h2 className="sr-only">Unsaved changes</h2>
      </Dialog.Title>
      <Dialog.Description asChild>
        <p>Your outfit is on — but not saved as a Look yet.</p>
      </Dialog.Description>
      <div className="actions unsaved-guard-actions">
        <button type="button" className="btn-primary" onClick={onSave}>Save</button>
        <button type="button" className="btn-destructive-quiet" onClick={onDiscard}>Discard</button>
        <Dialog.Close asChild>
          <button type="button" className="btn-quiet">Keep editing</button>
        </Dialog.Close>
      </div>
    </BottomSheet>
  );
}
